import { FormattedMessage, IntlProvider } from 'react-intl';
import React from 'react';
import PropTypes from 'prop-types';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import { Surface } from '@americanexpress/dls-react';
import { RenderModule } from 'holocron';
import { useSelector } from 'react-redux';
import { getActiveLocaleSelector, getIsLoggedInSelector } from '@americanexpress/travel-selectors';
import { hasHotelCreditPage } from '../constants/featureRuleConstants';
import Seo from './Seo/Seo';
import Header from './PageHeader/Header';
import Steps from './Steps/Steps';
import Search from './Search/Search';
import Questions from './Questions/Questions';
import Benefits from './BenefitCards/BenefitCards';
import { useTravelCreditOneCMS } from '../fetchye/travelCreditOneCMSFetchye';
import { loadModuleData } from '../thunks/loadModuleData';
import HotelCreditModal from './HotelCreditModal/HotelCreditModal';

const TravelCredit = ({
  languageData,
  isOverlay: renderHotelCreditModal,
  isWaitingForMultipleRewards,
}) => {
  const locale = useSelector(getActiveLocaleSelector);
  const isHotelCreditMarket = useConditionalFeature(hasHotelCreditPage);
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const { isLoading, error } = useTravelCreditOneCMS();
  const hasLoadedData = !isLoading && !error && Object.entries(languageData).length > 0;

  // set 'ONE_MOCK_LOGIN' to 1 and 'isOverlay' to true in package.json to render modal locally
  if (renderHotelCreditModal) {
    const shouldShowModal = hasLoadedData && isHotelCreditMarket && isLoggedIn;
    return (
      shouldShowModal ? (
        <IntlProvider locale={locale} messages={languageData}>
          <HotelCreditModal isWaitingForMr={isWaitingForMultipleRewards} />
        </IntlProvider>
      ) : null
    );
  }

  const Loading = (
    <div className="text-align-center pad-3">
      <div role="progressbar" className="progress-circle progress-indeterminate progress-lg" />
    </div>
  );

  if (Object.entries(languageData).length === 0 || isLoading) {
    return Loading;
  }

  if (error || !isHotelCreditMarket) {
    return (
      <IntlProvider locale={locale} messages={languageData}>
        <div className="container margin-tb dls-white-bg text-align-center alert-dialog" role="alert">
          <span className="icon icon-lg dls-icon-warning dls-red" />
          <h2 className="dls-gray-05 margin-1-tb">
            <FormattedMessage id="warningMessageText" />
          </h2>
        </div>
      </IntlProvider>
    );
  }

  return (
    <IntlProvider locale={locale} messages={languageData}>
      <Surface variant="foreground">
        <Seo />
        <RenderModule id="advisoryContainer" moduleName="axp-travel-advisory" />
        <Header />
        <Benefits />
        <Steps />
        <Search />
        <Questions />
        <RenderModule
          moduleName="axp-travel-terms-and-conditions"
          props={{ programs: ['hotelcredit'] }}
        />
      </Surface>
    </IntlProvider>
  );
};

export const TestableTravelCredit = TravelCredit;

TravelCredit.propTypes = {
  isOverlay: PropTypes.bool,
  isWaitingForMultipleRewards: PropTypes.bool,
  languageData: PropTypes.shape({}).isRequired,
};

TravelCredit.defaultProps = {
  isOverlay: false,
  isWaitingForMultipleRewards: false,
};

TravelCredit.holocron = {
  name: 'axp-travel-credit',
  loadModuleData,
};

export default oneAppModuleWrapper('axp-travel-credit')(
  TravelCredit
);
